import { Paper, Box, Typography, useTheme, alpha, Skeleton, Chip } from '@mui/material'
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell } from 'recharts'
import { useTranslation } from '@i18n'
import { useMemo } from 'react'

// ── Distinct department workload colors ──
const PENDING_COLOR = '#f59e0b'  // Amber
const COMPLETED_COLOR = '#10b981' // Emerald green

const DepartmentWorkloadChart = ({ data = [], isLoading = false }) => {
    const { t, language } = useTranslation()
    const theme = useTheme()

    const bgPaper = theme.palette.background.paper
    const primaryColor = theme.palette.primary.main
    const dividerColor = theme.palette.divider
    const textSecondary = theme.palette.text.secondary

    // Transform data based on language
    const chartData = useMemo(() => {
        if (!data || data.length === 0) return []
        return data.map(item => ({
            name: language === 'ar' ? (item.name_ar || item.name) : item.name,
            pending: item.pending,
            completed: item.completed
        }))
    }, [data, language])

    // Summary counts
    const totals = useMemo(() => {
        return chartData.reduce((acc, item) => ({
            pending: acc.pending + (item.pending || 0),
            completed: acc.completed + (item.completed || 0)
        }), { pending: 0, completed: 0 })
    }, [chartData])

    const CustomTooltip = ({ active, payload, label }) => {
        if (active && payload && payload.length) {
            const total = payload.reduce((sum, p) => sum + (p.value || 0), 0)
            return (
                <Box
                    sx={{
                        bgcolor: alpha(bgPaper, 0.95),
                        backdropFilter: 'blur(20px)',
                        p: 2,
                        borderRadius: 2,
                        boxShadow: `0 8px 32px ${alpha(theme.palette.common.black, 0.12)}`,
                        border: `1px solid ${alpha(dividerColor, 0.1)}`,
                        direction: language === 'ar' ? 'rtl' : 'ltr',
                        minWidth: 160
                    }}
                >
                    <Typography variant="body2" fontWeight={700} color="text.primary" gutterBottom>{label}</Typography>
                    {payload.map((entry, index) => (
                        <Box key={index} sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                            <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: entry.color }} />
                            <Typography variant="body2" sx={{ fontWeight: 500, flex: 1 }}>
                                {entry.name}
                            </Typography>
                            <Typography variant="body2" sx={{ color: entry.color, fontWeight: 700 }}>
                                {entry.value}
                            </Typography>
                        </Box>
                    ))}
                    <Box sx={{ mt: 1, pt: 1, borderTop: `1px solid ${alpha(dividerColor, 0.2)}` }}>
                        <Typography variant="caption" color="text.secondary" fontWeight={600}>
                            {t('stats.total', 'Total')}: {total}
                        </Typography>
                    </Box>
                </Box>
            )
        }
        return null
    }

    const hasData = chartData.length > 0
    const isRTL = language === 'ar'

    return (
        <Paper
            elevation={0}
            sx={{
                p: 3,
                borderRadius: 4,
                height: '100%',
                direction: isRTL ? 'rtl' : 'ltr',
                background: theme.palette.mode === 'dark'
                    ? `linear-gradient(135deg, ${alpha(bgPaper, 0.9)} 0%, ${alpha(primaryColor, 0.05)} 100%)`
                    : `linear-gradient(135deg, ${bgPaper} 0%, ${alpha(primaryColor, 0.02)} 100%)`,
                backdropFilter: 'blur(20px)',
                border: `1px solid ${alpha(dividerColor, 0.08)}`,
                boxShadow: `0 4px 24px ${alpha(theme.palette.common.black, 0.06)}`,
                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                '&:hover': {
                    boxShadow: `0 8px 40px ${alpha(primaryColor, 0.15)}`,
                    transform: 'translateY(-2px)'
                }
            }}
        >
            {/* Header with summary chips */}
            <Box sx={{ mb: 2, textAlign: isRTL ? 'right' : 'left' }}>
                <Typography variant="h6" fontWeight={700} color="text.primary" sx={{ mb: 0.5 }}>
                    {t('dashboard.departmentWorkload', 'Department Workload')}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                    {t('dashboard.workloadDescription', 'Pending vs completed by department')}
                </Typography>
                {hasData && !isLoading && (
                    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                        <Chip
                            size="small"
                            label={`${totals.pending} ${t('stats.pendingDepartment', 'Pending')}`}
                            sx={{
                                bgcolor: alpha(PENDING_COLOR, 0.12),
                                color: PENDING_COLOR,
                                fontWeight: 700,
                                fontSize: '0.7rem',
                                height: 24,
                                '& .MuiChip-label': { px: 1.5 }
                            }}
                        />
                        <Chip
                            size="small"
                            label={`${totals.completed} ${t('stats.replied', 'Completed')}`}
                            sx={{
                                bgcolor: alpha(COMPLETED_COLOR, 0.12),
                                color: COMPLETED_COLOR,
                                fontWeight: 700,
                                fontSize: '0.7rem',
                                height: 24,
                                '& .MuiChip-label': { px: 1.5 }
                            }}
                        />
                    </Box>
                )}
            </Box>

            <Box sx={{ height: 240 }}>
                {isLoading ? (
                    <Skeleton variant="rectangular" height="100%" sx={{ borderRadius: 2 }} />
                ) : hasData ? (
                    <ResponsiveContainer width="100%" height="100%">
                        <BarChart
                            data={chartData}
                            layout="vertical"
                            margin={isRTL
                                ? { top: 5, right: 145, left: 20, bottom: 5 }
                                : { top: 5, right: 20, left: 145, bottom: 5 }
                            }
                        >
                            <defs>
                                <linearGradient id="pendingGrad" x1="0" y1="0" x2="1" y2="0">
                                    <stop offset="0%" stopColor={PENDING_COLOR} stopOpacity={0.7} />
                                    <stop offset="100%" stopColor={PENDING_COLOR} stopOpacity={1} />
                                </linearGradient>
                                <linearGradient id="completedGrad" x1="0" y1="0" x2="1" y2="0">
                                    <stop offset="0%" stopColor={COMPLETED_COLOR} stopOpacity={0.7} />
                                    <stop offset="100%" stopColor={COMPLETED_COLOR} stopOpacity={1} />
                                </linearGradient>
                            </defs>
                            <CartesianGrid strokeDasharray="3 3" stroke={alpha(dividerColor, 0.4)} horizontal={false} />
                            <XAxis
                                type="number"
                                stroke={textSecondary}
                                fontSize={11}
                                tickLine={false}
                                axisLine={false}
                                orientation={isRTL ? 'top' : 'bottom'}
                                reversed={isRTL}
                            />
                            <YAxis
                                dataKey="name"
                                type="category"
                                stroke={textSecondary}
                                fontSize={11}
                                tickLine={false}
                                axisLine={false}
                                width={140}
                                orientation={isRTL ? 'right' : 'left'}
                            />
                            <Tooltip content={<CustomTooltip />} cursor={{ fill: alpha(primaryColor, 0.04) }} />
                            <Bar
                                dataKey="pending"
                                name={t('stats.pendingDepartment', 'Pending')}
                                fill="url(#pendingGrad)"
                                radius={[0, 6, 6, 0]}
                                barSize={16}
                                animationDuration={800}
                            />
                            <Bar
                                dataKey="completed"
                                name={t('stats.replied', 'Completed')}
                                fill="url(#completedGrad)"
                                radius={[0, 6, 6, 0]}
                                barSize={16}
                                animationDuration={800}
                            />
                        </BarChart>
                    </ResponsiveContainer>
                ) : (
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
                        <Typography variant="body2" color="text.secondary">
                            {t('common.noData', 'No data available')}
                        </Typography>
                    </Box>
                )}
            </Box>

            {/* Legend */}
            {hasData && !isLoading && (
                <Box sx={{ display: 'flex', justifyContent: 'center', gap: 4, mt: 1.5 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                        <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: PENDING_COLOR, boxShadow: `0 0 8px ${alpha(PENDING_COLOR, 0.4)}` }} />
                        <Typography variant="caption" color="text.secondary" fontWeight={600}>
                            {t('stats.pendingDepartment', 'Pending')}
                        </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                        <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: COMPLETED_COLOR, boxShadow: `0 0 8px ${alpha(COMPLETED_COLOR, 0.4)}` }} />
                        <Typography variant="caption" color="text.secondary" fontWeight={600}>
                            {t('stats.replied', 'Completed')}
                        </Typography>
                    </Box>
                </Box>
            )}
        </Paper>
    )
}

export default DepartmentWorkloadChart
